/**
 * ConvexErrorBoundary — scoped, signal-aware React error boundary.
 *
 * Replaces the legacy application-wide boundary that rendered the hardcoded
 * "Couldn't load notifications" copy on every captured error. The new
 * boundary:
 *
 *   - Captures the error in `getDerivedStateFromError` and extracts a stable
 *     `errorSignal` from either `error.data.kind` (the ConvexError shape) or
 *     by pattern-matching the message for the known validation patterns
 *     ("ArgumentValidationError" / "missing the required field"). Unrecognised
 *     errors fall back to the canonical "server-error" signal — never the
 *     legacy "notifications" copy.
 *
 *   - Forwards the resolved `errorSignal` to the canonical cross-cutting
 *     `ErrorState`, which itself resolves the contextual title/body via the
 *     central error map at `apps/dashboard/src/api/errorMap.ts`.
 *
 *   - Accepts a `scope` prop so per-route mounts label the failure context
 *     ("pipeline" / "admin" / "accessKeys" / "route" / "last-resort" /
 *     "notifications"); the scope is attached to the fallback DOM as
 *     `data-scope` so operators can disambiguate which area failed.
 *
 *   - Accepts an optional `label` prop (a short human-readable phrase for
 *     instrumentation and logs) and an `inline` prop that switches the
 *     forwarded `ErrorState` variant from "full" to "inline" — used by the
 *     notifications widget and other compact mounts.
 *
 *   - Emits exactly one structured log line under
 *     `event: "ui.error_boundary.capture"` carrying `{scope, errorSignal,
 *     correlationId}`. The legacy `notifications.stream.error` event name is
 *     gone. Logging is wrapped in its own try/catch so that a thrown logger
 *     (NFR-006 containment) cannot crash the boundary or escape to React.
 *
 *   - Provides a retry button via `ErrorState.onRetry`; pressing it clears
 *     the boundary's error state and re-renders children on the next pass.
 *
 * See:
 *   - specs/prd.md §3 FR-002 / FR-003 / NFR-006 (Capicúa v.1-D remediation).
 *   - specs/remediation-plan.md §2.1 (Fix A — Scope the error boundary).
 *   - apps/dashboard/src/components/layout/ConvexErrorBoundary.test.tsx
 *     (TDD red-phase tests from T301 that this implementation satisfies).
 */
import React from "react";
import { ErrorState } from "../cross-cutting/ErrorState";

/**
 * Recognised validation-message patterns. Convex throws
 * `ArgumentValidationError` for argument-validator failures and embeds the
 * phrase "missing the required field" in the message body when a required
 * argument is absent. Either signal resolves the boundary to the "validation"
 * copy from `ERROR_MAP`.
 */
const VALIDATION_PATTERNS: readonly string[] = [
  "ArgumentValidationError",
  "missing the required field",
];

/**
 * Public boundary props. All new fields are optional so existing call sites
 * that mount the boundary without a scope (none should exist after T302, but
 * the type stays permissive to ease migration) continue to compile.
 */
export interface ConvexErrorBoundaryProps {
  /** Short label naming the boundary's containment area. */
  scope?: string;
  /** Optional human-readable phrase, propagated to instrumentation. */
  label?: string;
  /** When true, render the fallback `<ErrorState>` in the compact "inline"
   * variant rather than the default "full" layout. */
  inline?: boolean;
  children: React.ReactNode;
}

interface ConvexErrorBoundaryState {
  hasError: boolean;
  /** The signal resolved from the caught error — `undefined` while clean. */
  errorSignal?: string;
}

/**
 * Resolve the `errorSignal` from a thrown value. Convex's `ConvexError`
 * instances expose a `data.kind` string; plain `Error` instances may carry a
 * validation phrase in `message`. Anything else returns `"server-error"`.
 *
 * Returning a stable string (rather than `undefined`) keeps the structured
 * log line uniform across captures and keeps the central error map the
 * single source of fallback copy.
 */
function resolveErrorSignal(error: unknown): string {
  // ConvexError shape: { data: { kind: string, ... }, ... }.
  if (
    error !== null &&
    typeof error === "object" &&
    "data" in error &&
    error.data !== null &&
    typeof error.data === "object" &&
    "kind" in (error as { data: Record<string, unknown> }).data
  ) {
    const kind = (error as { data: { kind: unknown } }).data.kind;
    if (typeof kind === "string" && kind.length > 0) {
      return kind;
    }
  }

  // Validation-message pattern match on `error.message`.
  if (
    error !== null &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    const message = (error as { message: string }).message;
    for (const pattern of VALIDATION_PATTERNS) {
      if (message.includes(pattern)) {
        return "validation";
      }
    }
  }

  return "server-error";
}

/**
 * Generate a 16-character lowercase-hex correlation identifier. Uses
 * `crypto.randomUUID` when available (modern browsers and jsdom >= 22), and
 * falls back to `Math.random` otherwise. The id is included in the structured
 * log line so operators can correlate a captured failure with its trace.
 */
function generateCorrelationId(): string {
  try {
    if (
      typeof globalThis !== "undefined" &&
      typeof globalThis.crypto !== "undefined" &&
      typeof globalThis.crypto.randomUUID === "function"
    ) {
      // randomUUID returns a 36-char string; collapse to a compact 16-char hex
      // prefix for log brevity while preserving uniqueness in practice.
      return globalThis.crypto.randomUUID().replace(/-/g, "").slice(0, 16);
    }
  } catch {
    // Fall through to the Math.random fallback below.
  }

  // Fallback: 16 hex chars from two Math.random draws.
  const a = Math.floor(Math.random() * 0xffffffff)
    .toString(16)
    .padStart(8, "0");
  const b = Math.floor(Math.random() * 0xffffffff)
    .toString(16)
    .padStart(8, "0");
  return `${a}${b}`;
}

/**
 * Per-route, signal-aware React error boundary.
 *
 * Mounted by:
 *   - `apps/dashboard/src/AppShell.tsx` once as the last-resort outer net
 *     (`scope="last-resort"`).
 *   - `apps/dashboard/src/AppShell.tsx` once per-route inside `PageShell`
 *     (`scope="route"`) when `scoped_error_boundary_v1` is on.
 *   - Optional inline mounts adjacent to compact widgets (notifications) that
 *     should fail isolated rather than blank their host frame.
 */
export class ConvexErrorBoundary extends React.Component<
  ConvexErrorBoundaryProps,
  ConvexErrorBoundaryState
> {
  constructor(props: ConvexErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  /**
   * Capture the resolved `errorSignal` alongside `hasError` so the render
   * branch and the structured log line agree on the signal without consulting
   * the caught value twice. Static method per the React error-boundary
   * contract.
   */
  static getDerivedStateFromError(error: unknown): ConvexErrorBoundaryState {
    return { hasError: true, errorSignal: resolveErrorSignal(error) };
  }

  /**
   * Emit the structured capture log. The full body is wrapped in try/catch so
   * a thrown `console.error` or `JSON.stringify` cannot escape to React, per
   * NFR-006 containment.
   */
  componentDidCatch(error: unknown, _errorInfo: React.ErrorInfo): void {
    try {
      const payload = {
        event: "ui.error_boundary.capture",
        level: "ERROR",
        scope: this.props.scope ?? "unknown",
        label: this.props.label ?? null,
        errorSignal: this.state.errorSignal ?? resolveErrorSignal(error),
        correlationId: generateCorrelationId(),
        ts_ms: Date.now(),
      };
      console.error(JSON.stringify(payload));
    } catch {
      // Intentional swallow — a broken logger must never crash the boundary
      // or surface as a thrown exception to the React reconciler.
    }
  }

  /**
   * Clear the boundary's error state so the next render attempts to mount
   * the children again. Bound as a class field so the JSX consumer in
   * `ErrorState` receives a stable reference across renders.
   */
  private readonly resetBoundary = (): void => {
    this.setState({ hasError: false, errorSignal: undefined });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      const variant: "inline" | "full" = this.props.inline === true
        ? "inline"
        : "full";
      const scopeAttr = this.props.scope ?? "";

      // The wrapping div carries `data-scope` so axe-style assertions can
      // confirm WHICH area failed without parsing the rendered copy.
      return (
        <div data-scope={scopeAttr}>
          <ErrorState
            errorSignal={this.state.errorSignal ?? "server-error"}
            onRetry={this.resetBoundary}
            variant={variant}
          />
        </div>
      );
    }
    return this.props.children;
  }
}

export default ConvexErrorBoundary;
